import { AnyStepData, Asset, Region, Step, VariantResource } from "../types";

/**
 * A generic base class for custom errors that assigns the name
 * of the error class automatically. All custom errors should extend this.
 */
abstract class CustomError extends Error {
    constructor(message) {
        super(message);
        this.name = this.constructor.name;
    }
}

/**
 * A custom class for categorising errors related to workflow configuration.
 */
abstract class ConfigurationError extends CustomError {
    constructor(message) {
        super(`ConfigurationError - ${message}`);
    }
}

/**
 * Thrown when an option was expected but not found in the workflow configuration or
 * the server returned a 404 for an option we expected.
 */
class OptionNotFoundError extends ConfigurationError {
    readonly optionId: string;
    constructor(step: Step<AnyStepData>) {
        super(`Option not Configured: ${step.stepTitle}`);
        this.optionId = step?.optionId || "N/A";
    }
}

/**
 * Thrown when a layout is not found for a given region entity. This
 * can occur when panels are deleted from a workflow but steps are still relying
 * on the layout being present.
 */
class LayoutNotFoundError extends ConfigurationError {
    readonly panelId: string;
    constructor(region: Region) {
        super(`Panel not Found: ${region.panelId}`);
        this.panelId = region?.panelId || "N/A";
    }
}

/**
 * Thrown when an asset is not found on a specific variant. This can occur when
 * the asset wasn't configured in hub and the variant is loaded in a workflow.
 */
class AssetNotFoundError extends ConfigurationError {
    readonly variant: VariantResource;
    constructor(variant: VariantResource) {
        super(`Asset not found for variant: ${variant.name}`);
        this.variant = variant;
    }
}

/**
 * Thrown when a resource is not found linked to a specific asset. This
 * can occur when generation of a resource fails or hasn't completed. Or may be
 * a sign of a misconfiguration.
 */
class ResourceNotFoundError extends ConfigurationError {
    readonly asset: Asset;
    constructor(asset: Asset) {
        super(`Resource not found for asset: ${asset.name}`);
        this.asset = asset;
    }
}

/**
 * Thrown when configuration is missing when it was expected. More generic
 * to cover remaining cases outside of the more specific ones such as options, variants & assets.
 */
class MisconfigurationError extends ConfigurationError {
    readonly step: Step<AnyStepData>;
    constructor(step: Step<AnyStepData>, message: string) {
        super(`Workflow Misconfiguration: ${step.stepName} - ${message}`);
        this.step = step;
    }
}

/**
 * A custom class for categorising errors related to implementation of core.
 */
abstract class ImplementationError extends CustomError {
    constructor(message) {
        super(`ImplementationError - ${message}`);
    }
}

/**
 * Thrown when we hit a case that we didn't expect to happen
 */
class UnhandledBehaviorError extends ImplementationError {
    constructor(message: string) {
        super(`Unhandled Behavior Encountered: ${message}`);
    }
}

/**
 * Thrown when we fail to parse something that we expected to be valid
 */
class ParseError extends ImplementationError {
    constructor(message: string) {
        super(`Parsing Error: ${message}`);
    }
}

/**
 * Thrown when we use the client in a way that is not supported by the platform.
 */
class ClientError extends ImplementationError {
    constructor(message: string) {
        super(`Client Error: ${message}`);
    }
}

/**
 * Thrown when we fail to generate a resource on the client. Generally this is
 * due to poor handling of device or browser specific requirements like memory limits.
 */
class ResourceGenerationError extends ImplementationError {
    constructor(message: string) {
        super(`Resource Generation Failed: ${message}`);
    }
}

// Config errors
export { OptionNotFoundError, LayoutNotFoundError, AssetNotFoundError, ResourceNotFoundError, MisconfigurationError };

// Implementation errors
export { UnhandledBehaviorError, ClientError, ResourceGenerationError, ParseError };
